import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BarChart3,
  BellRing,
  Bot,
  CalendarClock,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  LayoutDashboard,
  Mic,
  MicOff,
  Printer,
  Search,
  Send,
  Sparkles,
  Volume2,
  VolumeX,
  Wand2,
} from 'lucide-react';
import { useAuth } from '@/core/hooks';
import { useProductUX } from '@/app/components';
import {
  localCaregivers,
  localClients,
  localIncidents,
  localInvoices,
  localTasks,
  localVisits,
} from '@/core/services/local-api-fallback';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import { getStoredLiveCarerRecords, type LiveCarerTrackingRecord } from '@/utils/liveTelemetryApi';

type ReportPeriod = 'daily' | 'weekly' | 'monthly';
type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';

interface AdminAlert {
  id: string;
  title: string;
  severity: AlertSeverity;
  area: string;
  description: string;
  action: string;
  href: string;
}

interface AssistantMessage {
  id: string;
  role: 'admin' | 'assistant';
  text: string;
  createdAt: string;
}

interface GeneratedReport {
  id: string;
  title: string;
  period: ReportPeriod;
  createdAt: string;
  sections: Array<{ heading: string; lines: string[] }>;
}

interface SearchItem {
  id: string;
  type: string;
  title: string;
  subtitle: string;
  href: string;
  keywords: string;
}

interface BrowserSpeechRecognitionAlternative {
  transcript: string;
}

interface BrowserSpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: BrowserSpeechRecognitionAlternative;
}

interface BrowserSpeechRecognitionEvent {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: BrowserSpeechRecognitionResult;
  };
}

interface BrowserSpeechRecognitionErrorEvent {
  error: string;
}

interface BrowserSpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onend: (() => void) | null;
  onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onstart: (() => void) | null;
  abort: () => void;
  start: () => void;
  stop: () => void;
}

type BrowserSpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

const DASHBOARD_WIDGETS_KEY = 'admin:efficiency-dashboard-widgets';
const ASSISTANT_HISTORY_KEY = 'admin:efficiency-assistant-history';

const defaultWidgets = [
  'Urgent alerts',
  'Late and missing visits',
  'Unfinished tasks',
  'Finance exceptions',
  'Payroll checks',
  'Compliance watch',
  'Daily briefing',
  'End-of-day report',
];

const quickCommands = [
  'Which visits are late today?',
  'Show visits without check-in',
  'Which tasks are unfinished?',
  'Generate the morning briefing',
  'Generate the end-of-day report',
  'Show outstanding invoices',
  'Show live carers with stale signal',
  'Prepare the weekly report',
];

const integratedAiCommands = [
  'Which visits are late today?',
  'Create a draft care plan for the highest-risk client',
  'Create a draft incident follow-up for open incidents',
  'Create a draft family update for completed visits today',
  'Create a draft compliance gap report',
  'Detect current operational risks',
  'Recommend today’s schedule optimisation',
];

const severityStyles: Record<AlertSeverity, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
};

const statusStyles: Record<string, string> = {
  SCHEDULED: 'bg-slate-100 text-slate-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  PAST_DUE: 'bg-red-100 text-red-800',
  SENT: 'bg-blue-100 text-blue-800',
  PAID: 'bg-emerald-100 text-emerald-800',
  DRAFT: 'bg-slate-100 text-slate-700',
  PENDING_REVIEW: 'bg-amber-100 text-amber-800',
};

function getSpeechRecognitionConstructor(): BrowserSpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const browserWindow = window as typeof window & {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
  };
  return browserWindow.SpeechRecognition ?? browserWindow.webkitSpeechRecognition ?? null;
}

function todayIso(): string {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function parseVisitStart(visit: { scheduledDate: string; scheduledStartTime: string }): Date {
  return new Date(`${visit.scheduledDate}T${visit.scheduledStartTime}:00`);
}

function isLateVisit(visit: { scheduledDate: string; scheduledStartTime: string; status: string }): boolean {
  return visit.status === 'SCHEDULED' && parseVisitStart(visit).getTime() + 10 * 60 * 1000 < Date.now();
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function buildSearchItems(liveRecords: LiveCarerTrackingRecord[]): SearchItem[] {
  const clientItems = localClients.map((client) => ({
    id: client.id,
    type: 'Client',
    title: client.fullName,
    subtitle: `${client.clientNumber} - ${client.primaryAddress.city}, ${client.primaryAddress.postalCode}`,
    href: `/clients/${client.id}`,
    keywords: `${client.fullName} ${client.clientNumber} ${client.phone} ${client.email}`,
  }));

  const caregiverItems = localCaregivers.map((caregiver) => ({
    id: caregiver.id,
    type: 'Carer',
    title: caregiver.fullName,
    subtitle: `${caregiver.role} - ${caregiver.email}`,
    href: '/caregivers',
    keywords: `${caregiver.fullName} ${caregiver.email} ${caregiver.phone} ${caregiver.role}`,
  }));

  const visitItems = localVisits.map((visit) => ({
    id: visit.id,
    type: 'Visit',
    title: `${visit.clientName} - ${visit.serviceTypeName}`,
    subtitle: `${visit.scheduledDate} ${visit.scheduledStartTime} - ${visit.caregiverName}`,
    href: '/scheduling',
    keywords: `${visit.clientName} ${visit.caregiverName} ${visit.serviceTypeName} ${visit.status}`,
  }));

  const invoiceItems = localInvoices.map((invoice) => ({
    id: invoice.id,
    type: 'Invoice',
    title: `${invoice.invoiceNumber} - ${invoice.clientName}`,
    subtitle: `${invoice.status} - ${formatMoney(invoice.balanceDue)} outstanding`,
    href: `/billing/${invoice.id}`,
    keywords: `${invoice.invoiceNumber} ${invoice.clientName} ${invoice.status} ${invoice.payerName}`,
  }));

  const liveItems = liveRecords.map((record) => ({
    id: record.caregiverId,
    type: 'Live carer',
    title: record.caregiverName,
    subtitle: `${record.status} - ${record.currentTask ?? 'No active task'}`,
    href: '/live-carers',
    keywords: `${record.caregiverName} ${record.email} ${record.status} ${record.currentClient ?? ''} ${record.currentTask ?? ''}`,
  }));

  return [...clientItems, ...caregiverItems, ...visitItems, ...invoiceItems, ...liveItems];
}

function getUnfinishedTasks() {
  return localTasks.filter((task) => !['COMPLETED', 'CANCELLED'].includes(task.status));
}

function getOutstandingInvoices() {
  return localInvoices.filter((invoice) => invoice.balanceDue > 0);
}

function getStaleLiveRecords(liveRecords: LiveCarerTrackingRecord[]) {
  return liveRecords.filter((record) => Date.now() - new Date(record.lastUpdated).getTime() > 10 * 60 * 1000);
}

function buildAlerts(liveRecords: LiveCarerTrackingRecord[]): AdminAlert[] {
  const lateVisits = localVisits.filter(isLateVisit);
  const scheduledWithoutCheckIn = localVisits.filter((visit) => visit.status === 'SCHEDULED' && visit.scheduledDate <= todayIso());
  const unfinishedHighTasks = getUnfinishedTasks().filter((task) => task.priority === 'HIGH');
  const pastDueInvoices = localInvoices.filter((invoice) => invoice.status === 'PAST_DUE');
  const staleSignals = getStaleLiveRecords(liveRecords);
  const openIncidents = localIncidents.filter((incident) => !['CLOSED', 'RESOLVED'].includes(incident.status));

  return [
    lateVisits.length > 0 && {
      id: 'late-visits',
      title: `${lateVisits.length} late visit${lateVisits.length === 1 ? '' : 's'} today`,
      severity: 'critical' as const,
      area: 'Scheduling',
      description: lateVisits.map((visit) => `${visit.clientName} with ${visit.caregiverName}`).join(', '),
      action: 'Open scheduling and contact the assigned carer or reassign the visit.',
      href: '/scheduling',
    },
    scheduledWithoutCheckIn.length > 0 && {
      id: 'missing-checkin',
      title: `${scheduledWithoutCheckIn.length} scheduled visit${scheduledWithoutCheckIn.length === 1 ? '' : 's'} need check-in confirmation`,
      severity: 'high' as const,
      area: 'EVV',
      description: scheduledWithoutCheckIn.map((visit) => `${visit.scheduledStartTime} - ${visit.clientName}`).join(', '),
      action: 'Verify check-in status before payroll and billing are finalised.',
      href: '/time-tracking',
    },
    unfinishedHighTasks.length > 0 && {
      id: 'unfinished-tasks',
      title: `${unfinishedHighTasks.length} high-priority task${unfinishedHighTasks.length === 1 ? '' : 's'} unfinished`,
      severity: 'high' as const,
      area: 'Care delivery',
      description: unfinishedHighTasks.map((task) => task.title).join(', '),
      action: 'Review the task list and request carer updates before end of day.',
      href: '/tasks',
    },
    pastDueInvoices.length > 0 && {
      id: 'past-due-invoices',
      title: `${pastDueInvoices.length} invoice${pastDueInvoices.length === 1 ? '' : 's'} past due`,
      severity: 'medium' as const,
      area: 'Finance',
      description: `${formatMoney(pastDueInvoices.reduce((sum, invoice) => sum + invoice.balanceDue, 0))} outstanding.`,
      action: 'Open billing and follow up before the next payment cycle.',
      href: '/billing',
    },
    staleSignals.length > 0 && {
      id: 'stale-carer-signals',
      title: `${staleSignals.length} live carer signal${staleSignals.length === 1 ? '' : 's'} stale`,
      severity: 'medium' as const,
      area: 'Live carers',
      description: staleSignals.map((record) => record.caregiverName).join(', '),
      action: 'Refresh live telemetry and contact carers if location does not update.',
      href: '/live-carers',
    },
    openIncidents.length > 0 && {
      id: 'open-incidents',
      title: `${openIncidents.length} incident${openIncidents.length === 1 ? '' : 's'} need follow-up`,
      severity: 'medium' as const,
      area: 'Compliance',
      description: openIncidents.map((incident) => `${incident.clientName}: ${incident.incidentType}`).join(', '),
      action: 'Review incident status and close investigation actions.',
      href: '/incidents',
    },
  ].filter(Boolean) as AdminAlert[];
}

function buildBriefing(liveRecords: LiveCarerTrackingRecord[]): string[] {
  const todayVisits = localVisits.filter((visit) => visit.scheduledDate === todayIso());
  const lateVisits = localVisits.filter(isLateVisit);
  const activeLive = liveRecords.filter((record) => record.status === 'in-progress');
  const unfinishedTasks = getUnfinishedTasks();
  const outstandingInvoices = getOutstandingInvoices();
  const alerts = buildAlerts(liveRecords);

  return [
    `Today has ${todayVisits.length} planned visits, ${activeLive.length} live active carer signal${activeLive.length === 1 ? '' : 's'}, and ${lateVisits.length} late visit alert${lateVisits.length === 1 ? '' : 's'}.`,
    `${unfinishedTasks.length} care task${unfinishedTasks.length === 1 ? '' : 's'} remain unfinished. High-priority tasks should be reviewed before payroll and family updates.`,
    `${outstandingInvoices.length} invoice${outstandingInvoices.length === 1 ? '' : 's'} have outstanding balance, totalling ${formatMoney(outstandingInvoices.reduce((sum, invoice) => sum + invoice.balanceDue, 0))}.`,
    `${alerts.length} operational alert${alerts.length === 1 ? '' : 's'} are open. Start with critical scheduling and EVV issues first.`,
  ];
}

function buildEndOfDay(liveRecords: LiveCarerTrackingRecord[]): string[] {
  const completedVisits = localVisits.filter((visit) => visit.status === 'COMPLETED');
  const inProgressVisits = localVisits.filter((visit) => visit.status === 'IN_PROGRESS');
  const lateVisits = localVisits.filter(isLateVisit);
  const completedTasks = localTasks.filter((task) => task.status === 'COMPLETED');
  const activeLive = liveRecords.filter((record) => record.status === 'in-progress');

  return [
    `${completedVisits.length} visit${completedVisits.length === 1 ? '' : 's'} are completed and ${inProgressVisits.length} remain in progress.`,
    `${completedTasks.length} task${completedTasks.length === 1 ? '' : 's'} are completed; ${getUnfinishedTasks().length} still require review or carer update.`,
    `${activeLive.length} carer${activeLive.length === 1 ? '' : 's'} still show active live status.`,
    `${lateVisits.length} late scheduled visit${lateVisits.length === 1 ? '' : 's'} should be reconciled before the day is closed.`,
  ];
}

function buildReport(period: ReportPeriod, liveRecords: LiveCarerTrackingRecord[]): GeneratedReport {
  const alerts = buildAlerts(liveRecords);
  const outstandingInvoices = getOutstandingInvoices();
  const unfinishedTasks = getUnfinishedTasks();
  const lateVisits = localVisits.filter(isLateVisit);

  return {
    id: `report-${period}-${Date.now()}`,
    title: `Fuchsia ${period} administrator automation report`,
    period,
    createdAt: new Date().toISOString(),
    sections: [
      {
        heading: 'Executive summary',
        lines: [
          `${localVisits.length} visits reviewed, ${lateVisits.length} late visits, ${unfinishedTasks.length} unfinished tasks and ${alerts.length} operational alerts.`,
          `Outstanding invoice value: ${formatMoney(outstandingInvoices.reduce((sum, invoice) => sum + invoice.balanceDue, 0))}.`,
        ],
      },
      {
        heading: 'Visit control',
        lines: localVisits.map((visit) => `${visit.visitNumber}: ${visit.clientName}, ${visit.caregiverName}, ${visit.scheduledDate} ${visit.scheduledStartTime}, status ${visit.status}.`),
      },
      {
        heading: 'Urgent alerts',
        lines: alerts.length > 0 ? alerts.map((alert) => `${alert.severity.toUpperCase()}: ${alert.title}. Action: ${alert.action}`) : ['No urgent alerts detected.'],
      },
      {
        heading: 'Finance and payroll',
        lines: localInvoices.map((invoice) => `${invoice.invoiceNumber}: ${invoice.clientName}, ${invoice.status}, balance ${formatMoney(invoice.balanceDue)}.`),
      },
      {
        heading: 'Recommended next actions',
        lines: [
          'Resolve critical visit and check-in exceptions first.',
          'Review unfinished high-priority tasks before publishing family updates.',
          'Reconcile completed visits against payroll and billing before final PDF exports.',
          'Keep all AI-generated suggestions under administrator approval.',
        ],
      },
    ],
  };
}

function sanitizePdfText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E\n\r\t]/g, ' ')
    .replace(/[()\\]/g, (match) => `\\${match}`)
    .trim();
}

function wrapPdfText(text: string, maxLength: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function buildReportPdf(report: GeneratedReport): Blob {
  const rawLines = [
    report.title,
    'FUCHSIA HOME CARE CATALIN',
    'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
    `Generated: ${new Date(report.createdAt).toLocaleString('en-GB')}`,
    '',
    ...report.sections.flatMap((section) => [
      section.heading.toUpperCase(),
      ...section.lines.flatMap((line) => wrapPdfText(line, 92)),
      '',
    ]),
  ].map(sanitizePdfText);

  const linesPerPage = 56;
  const pages: string[][] = [];
  for (let index = 0; index < rawLines.length; index += linesPerPage) {
    pages.push(rawLines.slice(index, index + linesPerPage));
  }

  const objects: string[] = [];
  const addObject = (body: string) => {
    objects.push(body);
    return objects.length;
  };

  const catalogId = addObject('<< /Type /Catalog /Pages 2 0 R >>');
  const pagesId = addObject('');
  const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const pageIds: number[] = [];

  pages.forEach((pageLines) => {
    const content = [
      'BT',
      '/F1 10 Tf',
      '48 790 Td',
      '14 TL',
      ...pageLines.map((line, lineIndex) =>
        lineIndex === 0 ? `/F1 16 Tf (${line}) Tj /F1 10 Tf T*` : `(${line}) Tj T*`
      ),
      'ET',
    ].join('\n');
    const contentId = addObject(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    const pageId = addObject(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`
    );
    pageIds.push(pageId);
  });

  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;

  const chunks = ['%PDF-1.4\n'];
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(chunks.join('').length);
    chunks.push(`${index + 1} 0 obj\n${object}\nendobj\n`);
  });
  const xrefOffset = chunks.join('').length;
  chunks.push(`xref\n0 ${objects.length + 1}\n`);
  chunks.push('0000000000 65535 f \n');
  offsets.slice(1).forEach((offset) => {
    chunks.push(`${String(offset).padStart(10, '0')} 00000 n \n`);
  });
  chunks.push(`trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob(chunks, { type: 'application/pdf' });
}

function downloadReportPdf(report: GeneratedReport) {
  const url = URL.createObjectURL(buildReportPdf(report));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${report.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  toast.success('PDF report downloaded');
}

function printReportPdf(report: GeneratedReport) {
  const url = URL.createObjectURL(buildReportPdf(report));
  const pdfWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!pdfWindow) {
    URL.revokeObjectURL(url);
    toast.error('Allow pop-ups to print the PDF');
    return;
  }
  window.setTimeout(() => {
    pdfWindow.print();
    URL.revokeObjectURL(url);
  }, 700);
}

function answerCommand(query: string, liveRecords: LiveCarerTrackingRecord[], language: 'en' | 'ro' = 'en'): string {
  const normalized = query.toLowerCase();
  const lateVisits = localVisits.filter(isLateVisit);
  const missingCheckIn = localVisits.filter((visit) => visit.status === 'SCHEDULED' && visit.scheduledDate <= todayIso());
  const unfinishedTasks = getUnfinishedTasks();
  const outstandingInvoices = getOutstandingInvoices();
  const staleRecords = getStaleLiveRecords(liveRecords);
  const understood = language === 'ro'
    ? `Am înțeles că vrei să verific rapid: "${query}".`
    : `I understand that you want me to quickly check: "${query}".`;
  const recommendationsLabel = language === 'ro' ? 'Recomandări rapide' : 'Quick recommendations';

  if (normalized.includes('firma') || normalized.includes('company') || normalized.includes('gorleston') || normalized.includes('contact') || normalized.includes('telefon') || normalized.includes('program') || normalized.includes('servicii')) {
    if (language === 'en') {
      return [
        'I understand that you want details about Fuchsia Homecare Gorleston.',
        'Fuchsia Homecare Gorleston provides personalised home care focused on dignity, respect and independence.',
        'Address: Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN. Phone: 01493738488. On-call: 07443145811. Email: lowestoft@fuchsiahomecare.co.uk.',
        'Opening times: Monday-Friday 8:30-17:00; out of hours and weekends are emergency contact only.',
      ].join('\n');
    }
    return [
      'Am înțeles că vrei informații despre Fuchsia Homecare Gorleston.',
      'Fuchsia Homecare Gorleston oferă îngrijire personalizată la domiciliu, cu accent pe demnitate, respect și independență.',
      'Adresă: Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN. Tel: 01493738488. On-call: 07443145811. Email: lowestoft@fuchsiahomecare.co.uk.',
      'Program: Monday-Friday 8:30-17:00; în afara programului și weekend: contact de urgență.',
    ].join('\n');
  }

  if (normalized.includes('intarzi') || normalized.includes('late')) {
    const result = lateVisits.length
      ? `Late visits: ${lateVisits.map((visit) => `${visit.clientName} at ${visit.scheduledStartTime} with ${visit.caregiverName}`).join('; ')}.`
      : 'No late visits detected at this moment.';
    return `${understood}\n\n${recommendationsLabel}: ${lateVisits.length ? 'open Scheduling, contact the carer, and check whether the client or family must be informed.' : 'keep the live carer screen open and recheck before the next visit window.'}\n\nResult: ${result}`;
  }

  if (normalized.includes('check-in') || normalized.includes('checkin') || normalized.includes('fara check')) {
    const result = missingCheckIn.length
      ? `Visits requiring check-in confirmation: ${missingCheckIn.map((visit) => `${visit.clientName} (${visit.scheduledStartTime})`).join('; ')}.`
      : 'All scheduled visits currently have a completed or in-progress status.';
    return `${understood}\n\n${recommendationsLabel}: ${missingCheckIn.length ? 'verify EVV/GPS, contact the assigned carer, and do not approve payroll until the visit is confirmed.' : 'no immediate action; continue monitoring late check-ins.'}\n\nResult: ${result}`;
  }

  if (normalized.includes('task')) {
    const result = unfinishedTasks.length
      ? `Unfinished tasks: ${unfinishedTasks.map((task) => `${task.title} for ${localClients.find((client) => client.id === task.clientId)?.fullName ?? 'client'}`).join('; ')}.`
      : 'All tasks are completed.';
    return `${understood}\n\n${recommendationsLabel}: ${unfinishedTasks.length ? 'open the task list, prioritise high-risk care tasks, and ask carers to mark done/not done before check-out.' : 'no task escalation needed right now.'}\n\nResult: ${result}`;
  }

  if (normalized.includes('factur') || normalized.includes('invoice') || normalized.includes('restant')) {
    const result = outstandingInvoices.length
      ? `Outstanding invoices total ${formatMoney(outstandingInvoices.reduce((sum, invoice) => sum + invoice.balanceDue, 0))}: ${outstandingInvoices.map((invoice) => `${invoice.invoiceNumber} ${invoice.clientName}`).join('; ')}.`
      : 'No outstanding invoices detected.';
    return `${understood}\n\n${recommendationsLabel}: ${outstandingInvoices.length ? 'send reminders, check payment status, and prioritise the highest balance first.' : 'no billing follow-up needed from the current data.'}\n\nResult: ${result}`;
  }

  if (normalized.includes('carer') && (normalized.includes('live') || normalized.includes('semnal') || normalized.includes('locatie'))) {
    const result = staleRecords.length
      ? `Stale live carer signals: ${staleRecords.map((record) => `${record.caregiverName}, last update ${formatDateTime(record.lastUpdated)}`).join('; ')}.`
      : 'All live carer telemetry signals are fresh.';
    return `${understood}\n\n${recommendationsLabel}: ${staleRecords.length ? 'ask the carer to reopen the portal, enable GPS, and confirm their current task.' : 'continue normal monitoring.'}\n\nResult: ${result}`;
  }

  if (normalized.includes('briefing') || normalized.includes('dimineata')) {
    return `${understood}\n\n${recommendationsLabel}: review urgent alerts first, then late visits, unfinished tasks and billing exceptions.\n\nResult: ${buildBriefing(liveRecords).join(' ')}`;
  }

  if (normalized.includes('final') || normalized.includes('end-of-day') || normalized.includes('zi')) {
    return `${understood}\n\n${recommendationsLabel}: close urgent care actions first, then review payroll locks, open incidents and unpaid invoices.\n\nResult: ${buildEndOfDay(liveRecords).join(' ')}`;
  }

  if (normalized.includes('raport')) {
    return `${understood}\n\n${recommendationsLabel}: choose the shortest report that answers the task, then download the PDF for records.\n\nResult: I can generate daily, weekly and monthly administrator PDF reports from the report section below. Use the report buttons to create and download the PDF.`;
  }

  return `${understood}\n\n${recommendationsLabel}: ask one concrete question such as late visits, missing check-ins, unfinished tasks, outstanding invoices, live carers or daily briefing.\n\nResult: I reviewed visits, tasks, live carers, invoices and incidents. Current priority: ${buildAlerts(liveRecords)[0]?.title ?? 'no urgent operational alert detected'}.`;
}

export const AdminEfficiencyCenter: React.FC = () => {
  const { user, token } = useAuth();
  const { language } = useProductUX();
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const [command, setCommand] = useState('');
  const [search, setSearch] = useState('');
  const [liveRecords, setLiveRecords] = useState<LiveCarerTrackingRecord[]>(() => getStoredLiveCarerRecords([]));
  const [isAiConfigured, setIsAiConfigured] = useState(false);
  const [aiModel, setAiModel] = useState('local fallback');
  const [aiDatabaseContext, setAiDatabaseContext] = useState(false);
  const [humanApprovalRequired, setHumanApprovalRequired] = useState(true);
  const [pendingAiDrafts, setPendingAiDrafts] = useState(0);
  const [lastAiIntent, setLastAiIntent] = useState('No request yet');
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voiceReplyEnabled, setVoiceReplyEnabled] = useState(true);
  const [voiceStatus, setVoiceStatus] = useState('Voice assistant ready');
  const [messages, setMessages] = useState<AssistantMessage[]>(() =>
    getLocalStorage<AssistantMessage[]>(ASSISTANT_HISTORY_KEY, [
      {
        id: 'assistant-welcome',
        role: 'assistant',
        text: 'Hello. I am Fuchsia AI Assistant. I will answer calmly, briefly and practically, and I will adapt information to your role.',
        createdAt: new Date().toISOString(),
      },
    ])
  );
  const [widgets, setWidgets] = useState<string[]>(() => getLocalStorage<string[]>(DASHBOARD_WIDGETS_KEY, defaultWidgets));
  const [reports, setReports] = useState<GeneratedReport[]>([]);

  useEffect(() => {
    setVoiceSupported(Boolean(getSpeechRecognitionConstructor()) && 'speechSynthesis' in window);

    return () => {
      recognitionRef.current?.abort();
      window.speechSynthesis?.cancel();
    };
  }, []);

  useEffect(() => {
    const refreshLive = () => setLiveRecords(getStoredLiveCarerRecords([]));
    refreshLive();
    const timer = window.setInterval(refreshLive, 10_000);
    window.addEventListener('focus', refreshLive);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshLive);
    };
  }, []);

  useEffect(() => {
    const refreshAiDrafts = async () => {
      try {
        const draftsResponse = await fetch('/api/ai/drafts', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!draftsResponse.ok) throw new Error(`AI drafts returned ${draftsResponse.status}`);
        const draftsPayload = (await draftsResponse.json()) as {
          items?: Array<{ status?: string }>;
          total?: number;
        };
        const pending = draftsPayload.items?.filter((draft) => draft.status === 'PENDING_HUMAN_APPROVAL').length;
        setPendingAiDrafts(typeof pending === 'number' ? pending : draftsPayload.total ?? 0);
      } catch {
        setPendingAiDrafts(0);
      }
    };

    const checkAiStatus = async () => {
      try {
        const response = await fetch('/api/ai/status');
        if (!response.ok) throw new Error(`AI status returned ${response.status}`);
        const payload = (await response.json()) as {
          configured?: boolean;
          model?: string;
          databaseContext?: boolean;
          humanApprovalRequired?: boolean;
        };
        setIsAiConfigured(Boolean(payload.configured));
        setAiModel(payload.model || 'OpenAI');
        setAiDatabaseContext(Boolean(payload.databaseContext));
        setHumanApprovalRequired(payload.humanApprovalRequired !== false);
        await refreshAiDrafts();
      } catch {
        setIsAiConfigured(false);
        setAiModel('local fallback');
        setAiDatabaseContext(false);
      }
    };

    void checkAiStatus();
  }, [token]);

  useEffect(() => {
    setLocalStorage(ASSISTANT_HISTORY_KEY, messages.slice(-20));
  }, [messages]);

  useEffect(() => {
    setLocalStorage(DASHBOARD_WIDGETS_KEY, widgets);
  }, [widgets]);

  const alerts = useMemo(() => buildAlerts(liveRecords), [liveRecords]);
  const briefing = useMemo(() => buildBriefing(liveRecords), [liveRecords]);
  const endOfDay = useMemo(() => buildEndOfDay(liveRecords), [liveRecords]);
  const searchItems = useMemo(() => buildSearchItems(liveRecords), [liveRecords]);

  const filteredSearchItems = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return searchItems.slice(0, 8);
    return searchItems
      .filter((item) => `${item.title} ${item.subtitle} ${item.keywords}`.toLowerCase().includes(normalized))
      .slice(0, 12);
  }, [search, searchItems]);

  const stats = useMemo(() => {
    const outstandingInvoices = getOutstandingInvoices();
    return [
      { label: 'Open alerts', value: String(alerts.length), detail: 'urgent checks', icon: <BellRing className="h-5 w-5" /> },
      { label: 'Late visits', value: String(localVisits.filter(isLateVisit).length), detail: 'need action', icon: <CalendarClock className="h-5 w-5" /> },
      { label: 'Unfinished tasks', value: String(getUnfinishedTasks().length), detail: 'care delivery', icon: <CheckCircle2 className="h-5 w-5" /> },
      { label: 'Outstanding', value: formatMoney(outstandingInvoices.reduce((sum, invoice) => sum + invoice.balanceDue, 0)), detail: 'billing value', icon: <BarChart3 className="h-5 w-5" /> },
    ];
  }, [alerts.length]);

  const buildAiContext = () => ({
    generatedAt: new Date().toISOString(),
    language,
    languageInstruction: language === 'ro'
      ? 'Respond and speak in Romanian.'
      : 'Respond and speak in English.',
    company: {
      name: 'Fuchsia Homecare Gorleston',
      tagline: 'Always Caring, Always Here',
      address: 'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
      phone: '01493738488',
      onCall: '07443145811',
      email: 'lowestoft@fuchsiahomecare.co.uk',
      openingTimes: [
        'Monday - Friday: 8:30 - 17:00',
        'Monday - Friday: 17:00 - 8:30 emergency only contact',
        'Saturday - Sunday: emergency only contact',
      ],
      services: ['Hourly home care', 'Daily home care', 'Hospital to home care', 'Respite home care', 'Specialised care at home', 'Wheelchair transport services'],
    },
    metrics: {
      openAlerts: alerts.length,
      lateVisits: localVisits.filter(isLateVisit).length,
      unfinishedTasks: getUnfinishedTasks().length,
      outstandingInvoices: getOutstandingInvoices().length,
      outstandingInvoiceValue: getOutstandingInvoices().reduce((sum, invoice) => sum + invoice.balanceDue, 0),
    },
    alerts,
    briefing,
    endOfDay,
    visits: localVisits.map((visit) => ({
      id: visit.id,
      visitNumber: visit.visitNumber,
      clientName: visit.clientName,
      caregiverName: visit.caregiverName,
      scheduledDate: visit.scheduledDate,
      scheduledStartTime: visit.scheduledStartTime,
      status: visit.status,
      serviceType: visit.serviceTypeName,
      tasksCompleted: visit.tasksCompleted,
      tasksTotal: visit.tasksTotal,
    })),
    tasks: localTasks.map((task) => ({
      id: task.id,
      title: task.title,
      status: task.status,
      priority: task.priority,
      clientName: localClients.find((client) => client.id === task.clientId)?.fullName ?? 'Unknown client',
    })),
    invoices: localInvoices.map((invoice) => ({
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      clientName: invoice.clientName,
      status: invoice.status,
      balanceDue: invoice.balanceDue,
    })),
    incidents: localIncidents.map((incident) => ({
      id: incident.id,
      clientName: incident.clientName,
      incidentType: incident.incidentType,
      severity: incident.severity,
      status: incident.status,
    })),
    liveCarers: liveRecords.map((record) => ({
      caregiverName: record.caregiverName,
      status: record.status,
      currentClient: record.currentClient,
      currentTask: record.currentTask,
      scheduleMatched: record.scheduleMatched,
      payrollLocked: record.payrollLocked,
      lastUpdated: record.lastUpdated,
    })),
  });

  const effectiveRole = () => {
    const roles = user?.roles ?? [];
    if (roles.includes('SUPER_ADMIN')) return 'SYSTEM_OWNER';
    if (roles.includes('ADMIN')) return 'ADMIN';
    if (roles.includes('MANAGER')) return 'MANAGER';
    if (roles.includes('COORDINATOR')) return 'COORDINATOR';
    if (roles.includes('CARER') || roles.includes('CAREGIVER')) return 'CARER';
    if (roles.includes('FAMILY')) return 'FAMILY';
    return roles[0] ?? 'USER';
  };

  const requestServerAiAnswer = async (nextCommand: string): Promise<string | null> => {
    try {
      const response = await fetch('/api/ai/admin-assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question: nextCommand,
          role: effectiveRole(),
          language,
          page: '/admin-ai',
          context: buildAiContext(),
        }),
      });

      if (!response.ok) return null;

      const payload = (await response.json()) as {
        text?: string;
        configured?: boolean;
        model?: string;
        databaseContext?: boolean;
        intent?: string;
        humanApprovalRequired?: boolean;
        draft?: { status?: string };
      };
      if (payload.model) setAiModel(payload.model);
      setIsAiConfigured(Boolean(payload.configured));
      setAiDatabaseContext(Boolean(payload.databaseContext));
      setHumanApprovalRequired(payload.humanApprovalRequired !== false);
      if (payload.intent) setLastAiIntent(payload.intent.replace(/_/g, ' '));
      if (payload.draft?.status === 'PENDING_HUMAN_APPROVAL') {
        setPendingAiDrafts((current) => current + 1);
      }
      return payload.text?.trim() || null;
    } catch {
      return null;
    }
  };

  const speakAssistantText = (text: string) => {
    if (!voiceReplyEnabled || !('speechSynthesis' in window)) return;

    window.speechSynthesis.cancel();
    const spokenText = text.replace(/\s+/g, ' ').slice(0, 1800);
    const utterance = new SpeechSynthesisUtterance(spokenText);
    utterance.lang = language === 'ro' ? 'ro-RO' : 'en-GB';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.onstart = () => setVoiceStatus('Assistant is speaking...');
    utterance.onend = () => setVoiceStatus('Voice assistant ready');
    utterance.onerror = () => setVoiceStatus('Could not play the voice answer');
    window.speechSynthesis.speak(utterance);
  };

  const runCommand = async (rawCommand = command, options: { speak?: boolean } = {}) => {
    const nextCommand = rawCommand.trim();
    if (!nextCommand) return;

    const adminMessage: AssistantMessage = {
      id: `admin-${Date.now()}`,
      role: 'admin',
      text: nextCommand,
      createdAt: new Date().toISOString(),
    };

    setMessages((current) => [...current, adminMessage].slice(-20));
    setCommand('');
    setIsAiThinking(true);

    const serverAnswer = await requestServerAiAnswer(nextCommand);
    const assistantMessage: AssistantMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      text: serverAnswer ?? `${answerCommand(nextCommand, liveRecords, language)}\n\n${language === 'ro' ? 'AI-ul server nu este configurat momentan, deci răspunsul a folosit logica locală.' : 'Server AI is not configured yet, so this answer used the local fallback.'}`,
      createdAt: new Date().toISOString(),
    };

    setMessages((current) => [...current, assistantMessage].slice(-20));
    if (options.speak) speakAssistantText(assistantMessage.text);
    setIsAiThinking(false);
  };

  const replayLastAssistantAnswer = () => {
    const lastAssistantMessage = [...messages].reverse().find((message) => message.role === 'assistant');
    if (!lastAssistantMessage) {
      toast.error('No assistant answer available yet');
      return;
    }
    speakAssistantText(lastAssistantMessage.text);
  };

  const toggleVoiceAssistant = () => {
    if (!voiceSupported) {
      toast.error('Voice commands are not supported in this browser. Use Chrome or Edge and allow microphone access.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setVoiceStatus('Voice listening stopped');
      return;
    }

    const Recognition = getSpeechRecognitionConstructor();
    if (!Recognition) {
      toast.error('Speech recognition is not available in this browser');
      return;
    }

    const recognition = new Recognition();
    recognitionRef.current = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'ro-RO';

    recognition.onstart = () => {
      setIsListening(true);
      setVoiceStatus('Listening... speak your administrator command');
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const heardText = (finalTranscript || interimTranscript).trim();
      if (heardText) {
        setCommand(heardText);
        setVoiceStatus(finalTranscript ? `Heard: ${heardText}` : `Listening: ${heardText}`);
      }

      if (finalTranscript.trim()) {
        recognition.stop();
        void runCommand(finalTranscript.trim(), { speak: true });
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      setVoiceStatus(`Voice error: ${event.error}`);
      toast.error(event.error === 'not-allowed' ? 'Allow microphone access to use voice commands' : 'Voice command failed');
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    try {
      recognition.start();
    } catch {
      setIsListening(false);
      setVoiceStatus('Voice assistant could not start');
      toast.error('Voice assistant could not start');
    }
  };

  const createReport = (period: ReportPeriod) => {
    const report = buildReport(period, liveRecords);
    setReports((current) => [report, ...current].slice(0, 10));
    toast.success(`${period} PDF report prepared`);
  };

  const notifyUrgentAlerts = async () => {
    if (!('Notification' in window)) {
      toast.error('Browser notifications are not supported');
      return;
    }

    const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (permission !== 'granted') {
      toast.error('Notification permission was not granted');
      return;
    }

    alerts.slice(0, 3).forEach((alert) => {
      new Notification(alert.title, {
        body: `${alert.area}: ${alert.action}`,
        tag: alert.id,
      });
    });
    toast.success('Urgent notifications sent');
  };

  const toggleWidget = (widget: string) => {
    setWidgets((current) =>
      current.includes(widget) ? current.filter((item) => item !== widget) : [...current, widget]
    );
  };

  const isSuperAdmin = user?.roles.includes('SUPER_ADMIN') ?? false;

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-5">
            <div className="rounded-lg bg-[#6c3a80]/10 p-4 text-[#6c3a80]">
              <Sparkles className="h-8 w-8" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Administrator automation
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">AI Assistant and Time-Saving Control Center</h1>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                Ask operational questions, run natural-language commands, review daily briefings, generate PDF reports, search the application and monitor urgent alerts from one administrator page.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={notifyUrgentAlerts}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
          >
            <BellRing className="h-4 w-4" />
            Notify urgent alerts
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-500">{stat.label}</p>
              <span className="text-[#6c3a80]">{stat.icon}</span>
            </div>
            <p className="mt-3 text-2xl font-bold text-slate-950">{stat.value}</p>
            <p className="mt-1 text-sm text-slate-500">{stat.detail}</p>
          </div>
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-950">AI Assistant for quick questions</h2>
              <p className="mt-1 text-sm text-slate-600">
                {isAiConfigured
                  ? `Connected to OpenAI through the backend. Model: ${aiModel}.`
                  : `OpenAI is not active yet. Server still answers from the real database using protected fallback logic. Model setting: ${aiModel}.`}
              </p>
            </div>
            <Bot className="h-6 w-6 text-[#6c3a80]" />
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Real database context</p>
              <p className="mt-1 text-sm font-bold text-emerald-900">{aiDatabaseContext ? 'On' : 'Checking'}</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Human approval</p>
              <p className="mt-1 text-sm font-bold text-amber-900">{humanApprovalRequired ? 'Required' : 'Optional'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending AI drafts</p>
              <p className="mt-1 text-sm font-bold text-slate-950">{pendingAiDrafts}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last intent</p>
              <p className="mt-1 truncate text-sm font-bold capitalize text-slate-950">{lastAiIntent}</p>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-2 lg:flex-row">
            <input
              type="text"
              value={command}
              onChange={(event) => setCommand(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') runCommand();
              }}
              placeholder="Ask: Which visits are late today?"
              disabled={isAiThinking}
              className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
            />
            <button
              type="button"
              onClick={toggleVoiceAssistant}
              disabled={isAiThinking && !isListening}
              aria-pressed={isListening}
              className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition ${
                isListening
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'border border-[#6c3a80]/30 bg-[#6c3a80]/10 text-[#6c3a80] hover:bg-[#6c3a80]/15'
              }`}
              title="Speak an administrator command"
            >
              {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              {isListening ? 'Stop voice' : 'Voice command'}
            </button>
            <button
              type="button"
              onClick={() => runCommand()}
              disabled={isAiThinking}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
            >
              <Send className="h-4 w-4" />
              {isAiThinking ? 'Thinking' : 'Ask'}
            </button>
          </div>

          <div className="mt-3 flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-2">
              {voiceReplyEnabled ? <Volume2 className="h-4 w-4 shrink-0 text-[#6c3a80]" /> : <VolumeX className="h-4 w-4 shrink-0 text-slate-400" />}
              <span className="truncate">
                {voiceSupported ? voiceStatus : 'Voice commands need Chrome or Edge with microphone permission.'}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setVoiceReplyEnabled((current) => !current)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Voice reply {voiceReplyEnabled ? 'On' : 'Off'}
              </button>
              <button
                type="button"
                onClick={replayLastAssistantAnswer}
                disabled={!voiceSupported}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Read last answer
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {integratedAiCommands.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => runCommand(item)}
                disabled={isAiThinking}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-[#6c3a80]/40 hover:bg-[#6c3a80]/5"
              >
                {item}
              </button>
            ))}
          </div>

          <div className="mt-5 max-h-[360px] space-y-3 overflow-y-auto rounded-lg bg-slate-50 p-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`rounded-lg border p-3 text-sm ${
                  message.role === 'admin'
                    ? 'ml-8 border-[#6c3a80]/20 bg-white text-slate-900'
                    : 'mr-8 border-slate-200 bg-white text-slate-700'
                }`}
              >
                <div className="mb-1 flex items-center justify-between gap-3">
                  <span className="font-semibold text-slate-950">{message.role === 'admin' ? 'Administrator' : 'Fuchsia AI Assistant'}</span>
                  <span className="text-xs text-slate-400">{formatDateTime(message.createdAt)}</span>
                </div>
                <p className="leading-6">{message.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Natural command bar</h2>
              <p className="mt-1 text-sm text-slate-600">
                One-click operational commands for routine admin checks.
              </p>
            </div>
            <Wand2 className="h-6 w-6 text-[#6c3a80]" />
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {quickCommands.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => runCommand(item)}
                disabled={isAiThinking}
                className="rounded-lg border border-slate-200 bg-white p-4 text-left text-sm font-semibold text-slate-800 shadow-sm transition hover:border-[#6c3a80]/40 hover:bg-[#6c3a80]/5"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <CalendarClock className="h-6 w-6 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Automatic morning briefing</h2>
          </div>
          <div className="mt-5 space-y-3">
            {briefing.map((line) => (
              <div key={line} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                {line}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <Clock className="h-6 w-6 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">End-of-day report</h2>
          </div>
          <div className="mt-5 space-y-3">
            {endOfDay.map((line) => (
              <div key={line} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                {line}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Automatic urgent alerts</h2>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Missing visits, late check-ins, unfinished tasks, billing exceptions, stale carer signals and incidents.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
            {alerts.length} open alert{alerts.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="mt-5 grid gap-3 xl:grid-cols-2">
          {alerts.map((alert) => (
            <a
              key={alert.id}
              href={alert.href}
              className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#6c3a80]/40 hover:bg-[#6c3a80]/5"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${severityStyles[alert.severity]}`}>
                  {alert.severity}
                </span>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{alert.area}</span>
              </div>
              <h3 className="mt-3 font-bold text-slate-950">{alert.title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{alert.description}</p>
              <p className="mt-3 text-sm font-semibold text-[#6c3a80]">{alert.action}</p>
            </a>
          ))}
          {alerts.length === 0 && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
              No urgent operational alert detected.
            </div>
          )}
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Automatic weekly and monthly reports</h2>
          </div>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Generate PDF-ready reports from visits, alerts, invoices, tasks and live carer telemetry.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            {(['daily', 'weekly', 'monthly'] as ReportPeriod[]).map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => createReport(period)}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold capitalize text-white transition hover:bg-[#5e3474]"
              >
                <FileText className="h-4 w-4" />
                {period} PDF
              </button>
            ))}
          </div>
          <div className="mt-5 space-y-3">
            {reports.map((report) => (
              <div key={report.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-slate-950">{report.title}</p>
                    <p className="mt-1 text-xs text-slate-500">{formatDateTime(report.createdAt)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => downloadReportPdf(report)}
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => printReportPdf(report)}
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <Printer className="h-4 w-4" />
                      Print PDF
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {reports.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                No generated reports yet.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <Search className="h-6 w-6 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Smart search across the application</h2>
          </div>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search clients, carers, visits, invoices, live status..."
            className="mt-5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
          />
          <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto">
            {filteredSearchItems.map((item) => (
              <a
                key={`${item.type}-${item.id}`}
                href={item.href}
                className="block rounded-lg border border-slate-200 bg-white p-3 transition hover:border-[#6c3a80]/40 hover:bg-[#6c3a80]/5"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{item.type}</span>
                    <p className="mt-2 font-semibold text-slate-950">{item.title}</p>
                    <p className="mt-1 text-sm text-slate-600">{item.subtitle}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="h-6 w-6 text-[#6c3a80]" />
          <h2 className="text-xl font-bold text-slate-950">Role-personalised admin dashboard</h2>
        </div>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Signed in as {user?.email ?? 'administrator'} with {isSuperAdmin ? 'owner' : 'admin'} view. Choose which administrator widgets stay visible.
        </p>
        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {defaultWidgets.map((widget) => (
            <label
              key={widget}
              className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm font-semibold text-slate-800"
            >
              <span>{widget}</span>
              <input
                type="checkbox"
                checked={widgets.includes(widget)}
                onChange={() => toggleWidget(widget)}
                className="h-4 w-4 rounded border-slate-300 text-[#6c3a80] focus:ring-[#6c3a80]"
              />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Operational snapshot</h2>
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3">Visit</th>
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Carer</th>
                <th className="px-3 py-3">Time</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Tasks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {localVisits.map((visit) => (
                <tr key={visit.id}>
                  <td className="px-3 py-3 font-semibold text-slate-900">{visit.visitNumber}</td>
                  <td className="px-3 py-3 text-slate-700">{visit.clientName}</td>
                  <td className="px-3 py-3 text-slate-700">{visit.caregiverName}</td>
                  <td className="px-3 py-3 text-slate-700">{visit.scheduledDate} {visit.scheduledStartTime}</td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyles[visit.status] ?? 'bg-slate-100 text-slate-700'}`}>
                      {visit.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{visit.tasksCompleted}/{visit.tasksTotal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
